import React, { useContext } from 'react';
import EDGrid from '../../../../../../../../../BaseComponents/GridComponents/EDGrid';
import EDCheckbox from '../../../../../../../../../BaseComponents/Checkboxes/EDCheckbox';
import { Theme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import { Denominations } from '../../../EkonomiskAnalysDenominations/EkonomiskAnalysDenominations';
import EkonomiskAnalysDetailSummationModeSelect from './EkonomiskAnalysDetailSummationModeSelect/EkonomiskAnalysDetailSummationModeSelect';
import { EkonomiskAnalysSideMenuContext } from '../../Context/EkonomiskAnalysSideMenuContext';
import EDStandardSelect from '../../../../../../../../../BaseComponents/Selects/StandardSelect/EDStandardSelect';
import { AnvandarInfo } from '../../../../Const/AnvandarInfo';
import shortid from 'shortid';
import EkonomiskAnalysSaveDBSettingMutation from '../../../../Queries/EkonomiskAnalysSaveDBSettingMutation/EkonomiskAnalysSaveDBSettingMutation';
import { EkonomiskAnalysContext } from '../../../Context/EkonomiskAnalysContext';
import EkonomiskAnalysBudgetSummationModeSelect from './EkonomiskAnalysBudgetSummationModeSelect/EkonomiskAnalysBudgetSummationModeSelect';

interface IProps {
  activeDatabaseName: string;
}

const useStyles = makeStyles()(
  (theme: Theme) =>
    ({
      container: {
        marginTop: theme.spacing(2),
        paddingLeft: 40,
        paddingRight: 40,
      },
      comparisonCheckboxWrapper: {
        paddingTop: 16,
      },
    } as const)
);

const decimalOptions: number[] = [0, 1, 2, 3, 4];

/**
 * En vy över övriga inställningar för analysen som inte hör till någon specifk kategori
 */
const EkonomiskAnalysOther: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = ({ activeDatabaseName }) => {
  const { classes } = useStyles();
  const {
    denomination,
    onDenominationChange,
    nrDecimals,
    decimalsChanged,
    showComparison,
    toggleShowComparison,
    newAnalysSettings,
    changeIncludeEndastBokfört,
    periodLocked,
    isLopandeAnalys,
    userInfo,
    onExkluderaPersonalkostnaderChanged,
  } = useContext(EkonomiskAnalysSideMenuContext);

  const ekonomiskAnalysContext = useContext(EkonomiskAnalysContext);

  return (
    <div className={classes.container}>
      {!newAnalysSettings.loading && (
        <>
          <div>
            <EDGrid container spacing={4}>
              <EDGrid item xs={6}>
                <EDStandardSelect<number>
                  label="Enhet"
                  value={denomination}
                  onChange={onDenominationChange}
                  items={Denominations}
                  margin="dense"
                />
              </EDGrid>
              <EDGrid item xs={6}>
                <EDStandardSelect<number>
                  label="Antal decimaler"
                  value={nrDecimals}
                  items={decimalOptions.map(option => ({
                    header: String(option),
                    value: option,
                  }))}
                  onChange={decimalsChanged}
                  margin="dense"
                  tooltip="Antal decimaler"
                />
              </EDGrid>
            </EDGrid>
            <div className={classes.comparisonCheckboxWrapper}>
              <EDCheckbox
                label="Visa jämförelsetal"
                checked={showComparison}
                onCheck={toggleShowComparison}
                color="primary"
              />
            </div>
            <div>
              {newAnalysSettings && (
                <EDCheckbox
                  label="Inkludera ej bokförda journaler"
                  tooltip={
                    periodLocked
                      ? 'Perioden är låst, siffrorna kommer inte ändras.'
                      : ''
                  }
                  checked={newAnalysSettings.other.includeEjBokfordaJournaler}
                  onCheck={changeIncludeEndastBokfört}
                  color="primary"
                  disabled={periodLocked}
                />
              )}
            </div>

            {userInfo && (
              <EkonomiskAnalysSaveDBSettingMutation>
                {saveDBSetting => (
                  <div>
                    <EDCheckbox
                      color="primary"
                      checked={userInfo.useZeroSums}
                      label="Visa nollsummeringar"
                      onCheck={checked => {
                        saveDBSetting(
                          AnvandarInfo.ekonomiskAnalysUseZeroSums.name,
                          {
                            id: shortid.generate(),
                            value: checked ? 'Y' : 'N',
                          }
                        );

                        ekonomiskAnalysContext.setUseZeroSums(
                          checked ? true : false
                        );
                      }}
                    />
                  </div>
                )}
              </EkonomiskAnalysSaveDBSettingMutation>
            )}

            <div>
              {newAnalysSettings && (
                <EDCheckbox
                  labelNode={
                    <span>
                      <div>Exkludera personalkostnader</div>
                      <span>(redovisningsgrupp)</span>
                    </span>
                  }
                  checked={newAnalysSettings.other.exkluderaPersonalKostnader}
                  onCheck={onExkluderaPersonalkostnaderChanged}
                  color="primary"
                />
              )}
            </div>
          </div>

          {isLopandeAnalys && (
            <>
              <div>
                <EkonomiskAnalysDetailSummationModeSelect />
              </div>

              <div>
                <EkonomiskAnalysBudgetSummationModeSelect />
              </div>
            </>
          )}

          <div>
            <EkonomiskAnalysSaveDBSettingMutation>
              {saveDBSetting => (
                <EDStandardSelect<boolean>
                  label="Jämför mot"
                  value={Boolean(
                    ekonomiskAnalysContext.harPeriodiseradBudget
                      ? userInfo?.preferHelarsjamforelse
                      : true
                  )}
                  items={[
                    { header: 'Helårsbudget', value: true },
                    {
                      header: 'Periodiserad budget',
                      value: false,
                      disabled: !ekonomiskAnalysContext.harPeriodiseradBudget,
                      disabledReason:
                        newAnalysSettings.dates.from.year === undefined
                          ? 'Felaktigt år angett för analysen'
                          : !ekonomiskAnalysContext.harPeriodiseradBudget
                          ? 'Året har ingen periodiserad budget'
                          : undefined,
                    },
                  ]}
                  onChange={newValue =>
                    saveDBSetting(
                      AnvandarInfo.ekonomiskAnalysPreferHelarsJamforelse.name,
                      {
                        id: shortid.generate(),
                        value: newValue ? 'Y' : 'N',
                      }
                    )
                  }
                  // disabled={forceHelarsjamforelse == null}
                  // disabledReason={
                  //   forceHelarsjamforelse == null
                  //     ? 'Analysen innehåller inga budgetkolumner'
                  //     : undefined
                  // }
                />
              )}
            </EkonomiskAnalysSaveDBSettingMutation>
          </div>

          {userInfo && (
            <EkonomiskAnalysSaveDBSettingMutation>
              {saveDBSetting => (
                <div>
                  <EDCheckbox
                    color="primary"
                    checked={userInfo.useCurrentDateAsStartDate}
                    labelNode={
                      <span>
                        <div>Analysera med innevarande år</div>
                        <span>och period som start</span>
                      </span>
                    }
                    onCheck={checked => {
                      saveDBSetting(
                        AnvandarInfo.useCurrentDateAsStartDate.name,
                        { id: shortid.generate(), value: checked ? 'Y' : 'N' }
                      );
                    }}
                  />
                </div>
              )}
            </EkonomiskAnalysSaveDBSettingMutation>
          )}
        </>
      )}
    </div>
  );
};

export default EkonomiskAnalysOther;
